import React, { Component } from "react";
import PropTypes from "prop-types";
import { PaymentModal } from "./PaymentModal";
import { LoanStatusSyncManager } from "../../services/LoanStatusSyncManager";
import { loanApi } from "@api/loans";
import { fineApi } from "@api/fine/fine";
import {
  validatePaymentReceipt,
  FILE_SIZE_LIMITS,
  formatFileSize,
} from "@utils/fileUtils";

const AUTO_CLOSE_DELAY = 2000;

export class PaymentSubmissionModal extends Component {
  constructor(props) {
    super(props);
    this.state = this.getInitialState();
  }

  getInitialState = () => ({
    paymentImage: null,
    paymentImagePreview: null,
    isPaymentLoading: false,
    paymentMessage: "",
    selectedBooks: [],
    calculatedTotal: 0,
    isUsingOverrides: false,
    autoReturnItems: true,
    processingStatus: {
      stage: "",
      message: "",
      progress: { current: 0, total: 0 },
    },
    loanStatusChanges: [],
  });

  componentDidUpdate(prevProps) {
    if (this.props.isOpen && !prevProps.isOpen) {
      this.initializeState();
    }
  }

  initializeState = () => {
    const { selectedPatron } = this.props;
    if (!selectedPatron) return;

    const overrideData = selectedPatron.overrideData;
    const { totalFine, selectedBooks, isUsingOverrides } =
      this.calculateInitialValues(overrideData);

    this.setState({
      ...this.getInitialState(),
      selectedBooks,
      calculatedTotal: totalFine,
      isUsingOverrides,
    });
  };

  calculateInitialValues = (overrideData) => {
    const { selectedPatron } = this.props;

    if (overrideData) {
      const selectedBooks = overrideData.selectedBooks || [];
      const totalFine = overrideData.useManualTotal
        ? overrideData.totalAmount || 0
        : selectedBooks.reduce(
            (total, bookIndex) => total + this.getBookFineAmount(bookIndex),
            0
          );

      return { totalFine, selectedBooks, isUsingOverrides: true };
    }

    const selectedBooks = selectedPatron.loans
      .map((loan, index) => (loan.calculatedFine > 0 ? index : null))
      .filter((index) => index !== null);

    return {
      totalFine: selectedPatron.totalFine || 0,
      selectedBooks,
      isUsingOverrides: false,
    };
  };

  getBookFineAmount = (bookIndex) => {
    const { selectedPatron } = this.props;
    const overrideData = selectedPatron.overrideData;

    if (overrideData?.individualAmounts) {
      const loanId = selectedPatron.loans[bookIndex].metadata.pid;
      const amount = overrideData.individualAmounts[loanId];
      if (amount !== undefined) return parseFloat(amount) || 0;
    }

    if (overrideData?.overrideAmounts?.[bookIndex] !== undefined) {
      return parseFloat(overrideData.overrideAmounts[bookIndex]) || 0;
    }

    return selectedPatron.loans[bookIndex].calculatedFine || 0;
  };

  handleBookSelection = (bookIndex, isChecked) => {
    const { selectedPatron } = this.props;
    const { selectedBooks } = this.state;

    if (selectedPatron.overrideData?.useManualTotal) {
      this.setState({
        paymentMessage:
          "Individual book selection is disabled when using manual total override.",
      });
      return;
    }

    const newSelectedBooks = isChecked
      ? [...selectedBooks, bookIndex]
      : selectedBooks.filter((index) => index !== bookIndex);

    const newTotal = newSelectedBooks.reduce(
      (total, index) => total + this.getBookFineAmount(index),
      0
    );

    this.setState({
      selectedBooks: newSelectedBooks,
      calculatedTotal: newTotal,
      paymentMessage: "",
    });
  };

  handleSelectAll = () => {
    const { selectedPatron } = this.props;
    const overrideData = selectedPatron.overrideData;

    if (overrideData?.useManualTotal) {
      this.setState({
        selectedBooks: overrideData.selectedBooks || [],
        calculatedTotal: overrideData.totalAmount || 0,
      });
      return;
    }

    const allBookIds = selectedPatron.loans.map((_, index) => index);
    const totalAmount = allBookIds.reduce(
      (total, index) => total + this.getBookFineAmount(index),
      0
    );

    this.setState({
      selectedBooks: allBookIds,
      calculatedTotal: totalAmount,
    });
  };

  handleDeselectAll = () => {
    if (this.props.selectedPatron.overrideData?.useManualTotal) {
      this.setState({
        paymentMessage: "Cannot deselect all when using manual total override.",
      });
      return;
    }

    this.setState({
      selectedBooks: [],
      calculatedTotal: 0,
      paymentMessage: "",
    });
  };

  validateImage = (file) => {
    // Use centralized validation
    const validation = validatePaymentReceipt(file);
    if (!validation.valid) {
      return validation.errors.join("; ");
    }
    return null;
  };

  handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const error = this.validateImage(file);
    if (error) {
      this.setState({ paymentMessage: error });
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      this.setState({
        paymentImage: file,
        paymentImagePreview: e.target.result,
        paymentMessage: "",
      });
    };
    reader.readAsDataURL(file);
  };

  updateProcessingStatus = (stage, message, current = 0, total = 0) => {
    this.setState({
      processingStatus: { stage, message, progress: { current, total } },
    });
  };

  createPaymentFormData = () => {
    const { selectedPatron } = this.props;
    const { paymentImage, selectedBooks, calculatedTotal } = this.state;

    const formData = new FormData();
    formData.append("profile_id", selectedPatron.pid);
    formData.append("amount", calculatedTotal.toFixed(2));
    formData.append("payment_receipt", paymentImage);

    const selectedLoanIds = selectedBooks.map(
      (index) => selectedPatron.loans[index].metadata.pid
    );
    formData.append("loan_ids", JSON.stringify(selectedLoanIds));

    const loanAmountDetails = selectedBooks.map((bookIndex) => ({
      loanId: selectedPatron.loans[bookIndex].metadata.pid,
      bookIndex,
      calculatedAmount: selectedPatron.loans[bookIndex].calculatedFine || 0,
      overrideAmount: this.getBookFineAmount(bookIndex),
      finalAmount: this.getBookFineAmount(bookIndex),
    }));

    formData.append("loan_amount_details", JSON.stringify(loanAmountDetails));

    if (selectedPatron.overrideData) {
      formData.append(
        "override_data",
        JSON.stringify(selectedPatron.overrideData)
      );
    }

    return { formData, selectedLoanIds };
  };

  handlePayFine = async () => {
    const { selectedPatron, onPaymentSubmit } = this.props;
    const { selectedBooks, autoReturnItems } = this.state;

    if (selectedBooks.length === 0) return;

    this.setState({ isPaymentLoading: true });
    this.updateProcessingStatus(
      "payment",
      "Processing payment...",
      0,
      autoReturnItems ? selectedBooks.length + 1 : 1
    );

    try {
      const { formData, selectedLoanIds } = this.createPaymentFormData();
      await onPaymentSubmit(formData);

      this.updateProcessingStatus(
        autoReturnItems ? "returning" : "complete",
        autoReturnItems
          ? "Payment successful! Returning items..."
          : "Payment processed successfully!",
        1,
        autoReturnItems ? selectedBooks.length + 1 : 1
      );

      if (autoReturnItems && selectedLoanIds.length > 0) {
        await this.processAutoReturn(selectedLoanIds, selectedPatron);
      }

      this.handlePaymentSuccess(autoReturnItems, selectedBooks.length);
    } catch (error) {
      this.handlePaymentError();
    }
  };

  handlePaymentSuccess = (autoReturnItems, bookCount) => {
    this.setState({
      paymentMessage: "Payment and return process completed successfully!",
      isPaymentLoading: false,
    });

    this.updateProcessingStatus(
      "complete",
      "All processes completed successfully!",
      autoReturnItems ? bookCount + 1 : 1,
      autoReturnItems ? bookCount + 1 : 1
    );

    setTimeout(() => this.props.onClose(), AUTO_CLOSE_DELAY);
  };

  handlePaymentError = () => {
    this.setState({
      isPaymentLoading: false,
      processingStatus: {
        stage: "error",
        message: "Payment processing failed",
        progress: { current: 0, total: 0 },
      },
    });
  };

  processAutoReturn = async (loanIds, selectedPatron) => {
    const paymentInfo = {
      paymentReference: `PAYMENT_${Date.now()}`,
      patronEmail: selectedPatron.email,
      patronName: selectedPatron.name,
    };

    try {
      const fineIds = await this.getFineIdsFromLoans(loanIds);

      if (fineIds.length === 0) {
        return await this.fallbackDirectLoanReturn(loanIds, paymentInfo);
      }

      const results =
        await LoanStatusSyncManager.completePaymentAndReturnWorkflow(
          fineIds,
          {
            payment_reference: paymentInfo.paymentReference,
            notes: `Auto-return after payment. Ref: ${paymentInfo.paymentReference}`,
            patron_email: paymentInfo.patronEmail,
            patron_name: paymentInfo.patronName,
          },
          this.handleWorkflowProgress,
          {
            autoReturn: true,
            returnData: {
              source: "fine_payment_modal",
              patron_email: paymentInfo.patronEmail,
            },
          }
        );

      this.processReturnResults(results, loanIds);
      return results;
    } catch (error) {
      this.handleAutoReturnError(loanIds, paymentInfo);
    }
  };

  handleWorkflowProgress = (progress) => {
    this.updateProcessingStatus(
      progress.stage || "processing",
      progress.message || "Processing...",
      progress.current || 0,
      progress.total || 0
    );
  };

  processReturnResults = (results, loanIds) => {
    const statusChanges =
      results.returns?.successful?.map((result) => ({
        loanId: result.loanId,
        previousStatus: result.previousStatus,
        newStatus: result.newStatus,
        success: result.success,
        timestamp: result.timestamp,
        verified: results.verification?.successful?.some(
          (v) => v.loanId === result.loanId
        ),
      })) || [];

    this.setState({
      loanStatusChanges: statusChanges,
    });

    this.updateProcessingStatus(
      "complete",
      `${
        results.returns?.successful?.length || 0
      } items returned successfully!`,
      loanIds.length,
      loanIds.length
    );

    if (
      results.returns?.successful?.length > 0 &&
      this.props.onLoanStatusUpdate
    ) {
      const returnedLoanIds = results.returns.successful.map(
        (result) => result.loanId
      );
      this.props.onLoanStatusUpdate(returnedLoanIds);
    }
  };

  handleAutoReturnError = async (loanIds, paymentInfo) => {
    this.setState({
      paymentMessage:
        "Payment successful, but some items may need to be returned manually.",
    });

    this.updateProcessingStatus(
      "warning",
      "Payment completed, but auto-return had issues.",
      loanIds.length,
      loanIds.length
    );

    return await this.fallbackDirectLoanReturn(loanIds, paymentInfo);
  };

  fallbackDirectLoanReturn = async (loanIds, paymentInfo) => {
    const results = {
      returns: { successful: [], failed: [], skipped: [] },
      verification: { successful: [], failed: [] },
      summary: { returnedLoans: 0, errors: 0 },
    };

    for (const loanId of loanIds) {
      try {
        const returnData = {
          transaction_date: new Date().toISOString(),
          notes: `Direct return after payment. Ref: ${paymentInfo.paymentReference}`,
          source: "payment_modal_fallback",
        };

        const response = await loanApi.doAction(
          loanId,
          "checkout-return",
          returnData
        );

        if (response.status === 200 || response.status === 202) {
          results.returns.successful.push({
            loanId,
            previousStatus: "ITEM_ON_LOAN",
            newStatus: "ITEM_RETURNED",
            success: true,
            timestamp: new Date().toISOString(),
          });
          results.summary.returnedLoans++;
        } else {
          throw new Error(`Unexpected response status: ${response.status}`);
        }
      } catch (error) {
        results.returns.failed.push({
          loanId,
          error: error.message,
          timestamp: new Date().toISOString(),
        });
        results.summary.errors++;
      }
    }

    return results;
  };

  getFineIdsFromLoans = async (loanIds) => {
    const { selectedPatron } = this.props;
    let fineIds = [];

    try {
      const patronFinesResponse = await fineApi.getUserFines(
        selectedPatron.email
      );

      if (patronFinesResponse.data?.hits?.length > 0) {
        const activeFines = patronFinesResponse.data.hits.filter(
          (fine) => fine.status === "active" || fine.status === "unpaid"
        );

        fineIds = loanIds
          .map((loanId) => {
            const matchingFine = activeFines.find(
              (fine) => fine.loan_id === loanId || fine.loanId === loanId
            );
            return matchingFine?.id;
          })
          .filter(Boolean);
      }

      if (fineIds.length === 0) {
        for (const loanId of loanIds) {
          try {
            const finesByLoanResponse = await fineApi.getFinesByLoanId(loanId);
            if (finesByLoanResponse.data?.hits?.length > 0) {
              const activeFines = finesByLoanResponse.data.hits.filter(
                (fine) => fine.status === "active" || fine.status === "unpaid"
              );
              activeFines.forEach((fine) => {
                if (!fineIds.includes(fine.id)) {
                  fineIds.push(fine.id);
                }
              });
            }
          } catch (error) {
            // Silently continue with next loan
          }
        }
      }
    } catch (error) {
      // Return empty array on error
    }

    return fineIds;
  };

  handleLoanStatusUpdate = (returnedLoanIds) => {
    if (this.props.onLoanStatusUpdate) {
      this.props.onLoanStatusUpdate(returnedLoanIds);
    }
    if (this.props.onDataRefresh) {
      this.props.onDataRefresh();
    }
  };

  handleAutoReturnToggle = (enabled) => {
    this.setState({ autoReturnItems: enabled });
  };

  getSelectedBooksForModal = () => {
    const { selectedPatron } = this.props;
    const { selectedBooks } = this.state;

    return selectedBooks.map((bookIndex) => {
      const loan = selectedPatron.loans[bookIndex];
      const metadata = loan.metadata || {};
      const document = metadata.document || {};
      const item = metadata.item || {};

      return {
        loanId: metadata.pid,
        itemBarcode: item.barcode,
        title: document.title || item.title,
        itemTitle: document.title || item.title,
        calculatedFine: this.getBookFineAmount(bookIndex),
        dueDate: metadata.end_date,
        metadata: {
          pid: metadata.pid,
          end_date: metadata.end_date,
          document: { title: document.title },
          item: { barcode: item.barcode, title: item.title },
          patron: metadata.patron,
        },
      };
    });
  };

  handleClose = () => {
    if (!this.state.isPaymentLoading) {
      this.props.onClose();
    }
  };

  render() {
    const { isOpen, selectedPatron } = this.props;
    const {
      isPaymentLoading,
      paymentImagePreview,
      calculatedTotal,
      isUsingOverrides,
      paymentMessage,
      autoReturnItems,
      processingStatus,
      loanStatusChanges,
    } = this.state;

    if (!selectedPatron) return null;

    const overrideData = selectedPatron.overrideData;
    const isManualTotal = overrideData?.useManualTotal;
    const booksForModal = this.getSelectedBooksForModal();

    return (
      <PaymentModal
        isOpen={isOpen}
        selectedPatron={selectedPatron}
        overrideData={overrideData}
        calculatedTotal={calculatedTotal}
        isUsingOverrides={isUsingOverrides}
        selectedBooks={booksForModal}
        paymentImagePreview={paymentImagePreview}
        paymentMessage={paymentMessage}
        isPaymentLoading={isPaymentLoading}
        isManualTotal={isManualTotal}
        onClose={this.handleClose}
        onBookSelection={this.handleBookSelection}
        onSelectAll={this.handleSelectAll}
        onDeselectAll={this.handleDeselectAll}
        onImageUpload={this.handleImageUpload}
        onPayFine={this.handlePayFine}
        getBookFineAmount={this.getBookFineAmount}
        onLoanStatusUpdate={this.handleLoanStatusUpdate}
        autoReturnItems={autoReturnItems}
        onAutoReturnToggle={this.handleAutoReturnToggle}
        processingStatus={processingStatus}
        loanStatusChanges={loanStatusChanges}
        fileValidationInfo={{
          maxSize: formatFileSize(FILE_SIZE_LIMITS.MAX_5MB),
          allowedTypes: "JPG, PNG",
        }}
      />
    );
  }
}

PaymentSubmissionModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  selectedPatron: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onPaymentSubmit: PropTypes.func.isRequired,
  onLoanStatusUpdate: PropTypes.func,
  onDataRefresh: PropTypes.func,
};

PaymentSubmissionModal.defaultProps = {
  selectedPatron: null,
  onLoanStatusUpdate: null,
  onDataRefresh: null,
};

export default PaymentSubmissionModal;
